import { Injectable } from '@angular/core';
import { LoggerService } from '@klickdata/core/application';
import { AuthService } from '@klickdata/core/auth';
import { ResponseData } from '@klickdata/core/http';
import { User, UserData } from '@klickdata/core/user';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { Resource, ResourceData } from './resource.model';
import { ResourceService } from './resource.service';

@Injectable({
    providedIn: 'root',
})
export class ResourceSocketService {
    constructor(
        protected resourceService: ResourceService,
        protected auth: AuthService,
        private readonly logger: LoggerService
    ) {}

    public listenToNewResource(event = 'NewResource'): Observable<Resource> {
        return this.auth
            .listenUserChannel(event)
            .pipe(map((res: ResponseData<ResourceData>) => this.resourceService.createResource(res.data)));
    }
    public listenToNewResourceFromCustomer(event = 'NewResource'): Observable<Resource> {
        return this.auth
            .listenCustomerChannel(event)
            .pipe(map((res: ResponseData<ResourceData>) => this.resourceService.createResource(res.data)));
    }
    public listenToUserResourceUpdates(event = 'UserResourceUpdateEvent'): Observable<Resource> {
        return this.auth
            .listenUserChannel(event)
            .pipe(map((res: ResponseData<ResourceData>) => this.resourceService.createResource(res.data)));
    }
    public listenToUserResourcesFromAi(event = 'PromptResourceCreatedEvent'): Observable<Resource> {
        return this.auth
            .listenUserChannel(event)
            .pipe(map((res: ResponseData<ResourceData>) => this.resourceService.createResource(res.data)));
    }
    public listenToResourceUpdates(params: {
        res_id: number;
        interval?: number;
        eager?: string[] | string;
    }): Observable<Resource> {
        const reqInterval = params.interval ?? 5000;
        return this.auth
            .listenPrivate(
                `resource.${params.res_id}`,
                'ResourceUpdatedEvent',
                this.resourceService.getResourceWithCompletionTime(params.res_id, reqInterval, params.eager),
                reqInterval
            )
            .pipe(
                tap((resource) => this.logger.log(`ResourceUpdatedEvent for resource ${params.res_id}`, resource)),
                filter((res: ResponseData<ResourceData>) => !!res?.data?.id),
                map((res: ResponseData<ResourceData>) => this.resourceService.createResource(res.data!))
            );
    }
    public listenToCurrentResourceEditors(res_id: number): Observable<User[]> {
        return this.resourceService.getCurrentResourceEditors(res_id).pipe(
            catchError(() => of([] as User[])),
            switchMap((initialEditors) =>
                this.auth
                    .listenPrivate<UserData[]>(
                        `resource.${res_id}`,
                        'ResourceEditors',
                        this.resourceService.getCurrentResourceEditorsWithCompletionTime(res_id, 5000)
                    )
                    .pipe(
                        filter((res) => Array.isArray(res?.data)),
                        map((res) => this.resourceService.mapResourceEditors(res.data)),
                        startWith(initialEditors),
                        tap((editors) =>
                            this.logger.log(
                                `ResourceEditors for resource ${res_id} (${editors.length})`,
                                editors.map((editor) => ({
                                    id: editor.id,
                                    name: editor.name,
                                    dominated_field: editor.dominated_field ?? null,
                                }))
                            )
                        )
                    )
            )
        );
    }
}
